/**
 * Generic Promise timeout wrapper.
 *
 * Races `p` against a timer of `ms` milliseconds. If the timer fires first,
 * rejects with `error` (when provided) or a generic Error mentioning `label`.
 *
 * The pending timer is cleared in BOTH outcomes (resolve and reject) via
 * `.finally()` so no setTimeout closure stays alive after the promise settles.
 *
 * Design D2: lives in src/shared/utils/ — generic async utility, not face-infra specific.
 */
export function withTimeout<T>(
  p: Promise<T>,
  ms: number,
  label: string,
  error?: Error,
): Promise<T> {
  let timerId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => {
      reject(error ?? new Error(`${label} timed out after ${ms}ms`));
    }, ms);
  });

  return Promise.race([p, timeout]).finally(() => clearTimeout(timerId));
}
